/* eslint-disable react-hooks/exhaustive-deps */
import { AppContext, Project } from "@/App";
import OverviewComponent, { ProjectOverviewRow } from "../Components/OverviewComponent";
import ProjectHealth from "../Components/ProjectHealth";
import { RadialChartComponent } from "../Components/RadialChartComponent";
import WorkstreamHealthReport from "../Components/WorkstreamHealth";
import WorkstreamProgressChart, { WorkstreamProgress } from "../Components/WorkstreamProgressChart";
import { useContext, useEffect } from "react";
import NoProjects from "./NoProjects";
import Loading from "@/Components/loading";
import { WorkstreamHealth } from "@/Components/StatusReport/StatusReportChart";
import React from "react";
import { RaidNotification } from "@/Components/Notifications";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/Components/ui/dialog";
import FrmMultiProjectDashboard from "@/Components/MultiProjectDashboard/frmMultiProjectDashboard";
import FrmDashViewOptions from "@/Components/MultiProjectDashboard/frmDashViewOptions";
import { RefreshIcon } from "@/Components/ui/Icons/TooltipIcon";
import { useIsMobile } from "@/hooks/use-mobile";
import { useResponsiveZoom } from "@/hooks/use-responsive-zoom";
import { useTheme } from "@/Components/theme-provider";
import WizardProvider, { WizardContext } from "@/Components/wizard/wizardProvider";
import { getBasicTourSteps } from "@/Components/MultiProjectDashboard/Wizards/basic_page_tour";
import TutorialChecker from "@/Components/Tutorial/TutorialChecker";
import { Tutorial } from "@/lib/utils";
import HelpLink from "@/Components/ui/help-link";
import { MultiProjectDashContext } from "@/Components/MultiProjectDashboard/MultiProjectDashboard";


export type WorkstreamOverview = {
    actual: number;
    planned: number;
    workstreamHealth: WorkstreamHealth; // name is part of the workstream health
}

export type DashboardData = {
    workstreams: WorkstreamOverview[];
    projectHealth: WorkstreamHealth;
    projectOverview: ProjectOverviewRow[]
    currentWeek: number;
    totalWeeks: number;
    notifications: RaidNotification[];
}

const MultiProjectDashboard = () => {
    const app = useContext(AppContext);
    const wizard = React.useContext(WizardContext);
    const isMobile = useIsMobile();
    const zoomLevel = useResponsiveZoom();
    const { theme } = useTheme();

    const [dialogTitle, setDialogTitle] = React.useState("Group Edit Tasks");
    const [dialogContent, setDialogContent] = React.useState<JSX.Element | null>(null);
    const [dialogOpen, setDialogOpen] = React.useState(false);

    const [isLoading, setIsLoading] = React.useState(true);
    const [demoMode, setDemoMode] = React.useState<boolean>(false);
    const [demoProjects, setDemoProjects] = React.useState<Project[]>([]);

    const [selectedProjects, setSelectedProjects] = React.useState<number[]>(app.applicationUser?.userSettings?.dashboardProjects || app.projects.map(p => p.id));
    const [selectedViewElements, setSelectedViewElements] = React.useState<number>(app.applicationUser?.userSettings?.dashboardElements || 31); // Default to all view elements selected (31 = 1+2+4+8+16)

    const [workstreamHealth, setWorkstreamHealth] = React.useState<WorkstreamHealth[]>([]);
    const [projectHealth, setProjectHealth] = React.useState<WorkstreamHealth>();
    const [projectProgressActual, setProjectProgressActual] = React.useState(0);
    const [projectProgressPlanned, setProjectProgressPlanned] = React.useState(0);
    const [workstreamProgress, setWorkstreamProgress] = React.useState<WorkstreamProgress[]>([]);
    const [projectOverview, setProjectOverview] = React.useState<ProjectOverviewRow[]>([]);
    const [currentWeek, setCurrentWeek] = React.useState(0);
    const [totalWeeks, setTotalWeeks] = React.useState(0);
    const [notifications, setNotifications] = React.useState<RaidNotification[]>([]);

    // Demo mode data
    const [demoDashboardData, setDemoDashboardData] = React.useState<DashboardData | null>(null);
    const [fullDemoDashboardData, setFullDemoDashboardData] = React.useState<DashboardData | null>(null); // Store unfiltered demo data

    const hasLoadedInitialData = React.useRef(false);
    const lastUserId = React.useRef<string | null>(null);

    useEffect(() => {
        // Reset if user changed (logout/login)
        if (app.applicationUser?.id !== lastUserId.current) {
            hasLoadedInitialData.current = false;
            lastUserId.current = app.applicationUser?.id || null;
        }

        // Skip if we've already initiated the initial load
        if (hasLoadedInitialData.current) {
            return;
        }

        // Wait for application user and projects to be available
        if (!app.applicationUser || app.projects.length === 0) {
            return;
        }

        hasLoadedInitialData.current = true;

        // If user settings exist with dashboard projects, use those
        if (app.applicationUser.userSettings?.dashboardProjects) {
            loadDashboardData(app.applicationUser.userSettings.dashboardProjects);
        } else {
            // Load user settings first
            app.loadUserSettings().then((data) => {
                const projectsToLoad = data?.dashboardProjects && data.dashboardProjects.length > 0
                    ? data.dashboardProjects
                    : app.projects.map(p => p.id);
                loadDashboardData(projectsToLoad);
                if (data?.dashboardElements) {
                    setSelectedViewElements(data.dashboardElements);
                }
            });
        }
    }, [app.applicationUser, app.projects]);

    function loadDashboardData(projects: number[] = selectedProjects) {
        if (app.applicationUser && app.applicationUser.projectUserRoles.length == 0) {
            setIsLoading(false);
            return;
        }

        if (selectedProjects.length === 0 && app.projects.length === 0) {
            setIsLoading(true);
            return;
        }

        // Clear previous data when selectedProject changes
        setWorkstreamProgress([]);
        setProjectProgressActual(0);
        setProjectProgressPlanned(0);
        setProjectHealth(undefined);
        setWorkstreamHealth([]);
        setProjectOverview([]);
        setCurrentWeek(0);
        setTotalWeeks(0);

        setIsLoading(true);

        // If in demo mode, use demo data
        if (demoMode && demoDashboardData) {
            processDashboardData(demoDashboardData);
            setSelectedProjects(projects);
            setIsLoading(false);
            return;
        }

        if (projects.length > 0) {
            const queryString = projects.map(id => `ids=${id}`).join('&');
            fetch(`Reports/MultiProjectDashboard?${queryString}`)
                .then(async (response) => {
                    if (!response.ok) {
                        const errorText = await response.text();
                        console.error("Server error:", response.status, errorText);
                        throw new Error(`Server returned ${response.status}: ${errorText.substring(0, 200)}`);
                    }
                    return response.json();
                })
                .then(data => {
                    processDashboardData(data);
                    setSelectedProjects(projects);
                    setIsLoading(false);
                })
                .catch((error) => {
                    console.error("Error fetching dashboard data:", error);
                    setIsLoading(false);
                });
        }
    }

    function processDashboardData(data: DashboardData) {
        setWorkstreamProgress(data.workstreams.map(ws => {
            return {
                name: ws.workstreamHealth.workstreamName ?? "",
                workstreamId: ws.workstreamHealth.workstreamId ?? 0,
                actual: ws.actual,
                planned: ws.planned,
                scheduleStatus: ws.workstreamHealth.schedule
            }
        }));

        const projectPlanned = data.workstreams.reduce((acc, ws) => acc + ws.planned, 0);
        const projectActual = data.workstreams.reduce((acc, ws) => acc + ws.actual, 0);


        setProjectProgressActual(Math.round(projectActual / data.workstreams.length));
        setProjectProgressPlanned(Math.round(projectPlanned / data.workstreams.length));

        setProjectHealth(data.projectHealth);

        setWorkstreamHealth(data.workstreams.map(ws => ws.workstreamHealth));
        setProjectOverview(data.projectOverview);

        setCurrentWeek(data.currentWeek);
        setTotalWeeks(data.totalWeeks);
        setNotifications(data.notifications || []);
    }

    function showDashboardOptions() {
        setDialogTitle("Select Projects");
        setDialogContent(
            <FrmMultiProjectDashboard
                setDialogOpen={setDialogOpen}
                selectedProjects={selectedProjects}
                setSelectedProjects={updateSelectedProjects}
                projects={demoMode ? demoProjects : undefined}
            />
        );
        setDialogOpen(true);
    }

    function updateSelectedProjects(newProjects: number[]) {
        setSelectedProjects(newProjects);
        if (app.applicationUser && !demoMode) {
            app.updateUserSettings("dashboardProjects", newProjects.join(','));
        }
        if (demoMode) {
            // In demo mode, filter the existing demo data
            if (fullDemoDashboardData) {
                const filteredData = filterDemoDataByProjects(fullDemoDashboardData, newProjects);
                processDashboardData(filteredData);
            }
        } else {
            loadDashboardData(newProjects);
        }
    }

    function showViewOptions() {
        setDialogTitle("View Options");
        setDialogContent(
            <FrmDashViewOptions
                setDialogOpen={setDialogOpen}
                selectedViewElements={selectedViewElements}
                setSelectedViewElements={updateSelectedViewElements}
            />
        );
        setDialogOpen(true);
    }

    function updateSelectedViewElements(newElements: number) {
        setSelectedViewElements(newElements);
        if (app.applicationUser && !demoMode) {
            app.updateUserSettings("dashboardElements", newElements.toString());
        }
    }

    function filterProjectsByHealth(status: 1 | 2 | 4, statusName: "overall" | "scope" | "schedule" | "resources" | "financial" | "other") {
        let filteredProjects: number[] = selectedProjects;

        switch (statusName) {
            case "overall":
                filteredProjects = workstreamHealth
                    .filter(item => item.overall === status)
                    .map(item => item.workstreamId);
                break;
            case "scope":
                filteredProjects = workstreamHealth
                    .filter(item => item.scope === status)
                    .map(item => item.workstreamId);
                break;
            case "schedule":
                filteredProjects = workstreamHealth
                    .filter(item => item.schedule === status)
                    .map(item => item.workstreamId);
                break;
            case "resources":
                filteredProjects = workstreamHealth
                    .filter(item => item.resources === status)
                    .map(item => item.workstreamId);
                break;
            case "financial":
                filteredProjects = workstreamHealth
                    .filter(item => item.financial === status)
                    .map(item => item.workstreamId);
                break;
            case "other":
                filteredProjects = workstreamHealth
                    .filter(item => item.other === status)
                    .map(item => item.workstreamId);
                break;
        }

        setSelectedProjects(filteredProjects);
        if (app.applicationUser && !demoMode) {
            app.updateUserSettings("dashboardProjects", filteredProjects.join(','));
        }
        if (demoMode) {
            // In demo mode, filter from the full demo data
            if (fullDemoDashboardData) {
                const filteredData = filterDemoDataByProjects(fullDemoDashboardData, filteredProjects);
                setDemoDashboardData(filteredData);
                processDashboardData(filteredData);
            }
        } else {
            loadDashboardData(filteredProjects);
        }
    }

    function resetFilter() {
        if (demoMode) {
            // In demo mode, select all demo projects and show all data
            const allDemoProjectIds = demoProjects.map(p => p.id);
            setSelectedProjects(allDemoProjectIds);
            if (fullDemoDashboardData) {
                setDemoDashboardData(fullDemoDashboardData);
                processDashboardData(fullDemoDashboardData);
            }
        } else {
            setSelectedProjects(app.projects.map(p => p.id));
            if (app.applicationUser) {
                app.updateUserSettings("dashboardProjects", app.projects.map(p => p.id).join(','));
            }
            loadDashboardData(app.projects.map(p => p.id));
        }
    }

    function radialClick() {
        if ((selectedViewElements & 8) === 8) {
            // add momentary glow to WorkstreamProgressChart
            const chartElement = document.getElementById('workstream-progress-chart');
            if (chartElement) {
                chartElement.classList.add('shadow-[0_0_10px_5px_rgba(255,255,255,0.5)]');
                setTimeout(() => {
                    chartElement.classList.remove('shadow-[0_0_10px_5px_rgba(255,255,255,0.5)]');
                }, 1000);
            }
        } else {
            setSelectedViewElements(selectedViewElements + 8);
        }
    }

    function generateDemoProjects(): Project[] {
        const projectNames = [
            "Digital Initiative",
            "Cloud Migration Project",
            "Customer Portal Redesign",
            "ERP System Upgrade",
            "Mobile App Development"
        ];

        return projectNames.map((name, index) => ({
            id: 1000 + index, // Use high IDs to avoid conflicts
            name: name,
            projectTypeId: 1
        }));
    }

    // Calculate overall project health based on demo workstreams
    const calculateWorstStatus = (statuses: number[]): 1 | 2 | 4 => {
        const hasRed = statuses.includes(1);
        const hasAmber = statuses.includes(2);
        return hasRed ? 1 : hasAmber ? 2 : 4;
    };

    function generateDemoDataForProjects(projectsList: Project[]): DashboardData {
        const demoWorkstreams: WorkstreamOverview[] = [];
        const demoProjectOverview: ProjectOverviewRow[] = [];
        const demoNotifications: RaidNotification[] = [];

        // Generate demo workstreams
        projectsList.forEach((project, projectIndex) => {
            const numWorkstreams = 1;

            for (let i = 0; i < numWorkstreams; i++) {
                const planned = Math.floor(Math.random() * 40) + 30; // 30-70%
                const actual = Math.floor(Math.random() * (planned + 20)); // Can be ahead or behind
                const scheduleStatus = actual >= planned ? 4 : actual >= planned - 10 ? 2 : 1;
                const scopeStatus = Math.random() < 0.7 ? 4 : Math.random() < 0.9 ? 2 : 1;
                const resourcesStatus = Math.random() < 0.7 ? 4 : Math.random() < 0.9 ? 2 : 1;
                const financialStatus = Math.random() < 0.8 ? 4 : Math.random() < 0.95 ? 2 : 1;
                const otherStatus = Math.random() < 0.9 ? 4 : 2;
                const overallStatus = [scheduleStatus, scopeStatus, resourcesStatus, financialStatus, otherStatus].includes(1) ? 1 :
                    [scheduleStatus, scopeStatus, resourcesStatus, financialStatus, otherStatus].includes(2) ? 2 : 4;

                demoWorkstreams.push({
                    actual: actual,
                    planned: planned,
                    workstreamHealth: {
                        workstreamName: `${project?.name || 'Project'}`,
                        workstreamId: project.id,
                        lifecycleStage: 3 as 1 | 2 | 3 | 4, // Just set to Execution for demo
                        overall: overallStatus,
                        scope: scopeStatus,
                        schedule: scheduleStatus,
                        resources: resourcesStatus,
                        financial: financialStatus,
                        other: otherStatus
                    }
                });
            }

            // Generate demo project overview (top risks/issues/support)
            const numOverviewItems = Math.floor(Math.random() * 3) + 1; // 1-3 items per project
            for (let i = 0; i < numOverviewItems; i++) {
                const sourceType = Math.floor(Math.random() * 3); // 0 = Risk, 2 = Issue, 4 = Support
                const source = sourceType === 0 ? 0 : sourceType === 1 ? 2 : 4;
                const rating = Math.random() < 0.3 ? 2 : Math.random() < 0.7 ? 1 : 0;

                demoProjectOverview.push({
                    workstreamName: `${project?.name || 'Demo Project'}`,
                    workstreamId: project?.id,
                    rating: rating as 0 | 1 | 2,
                    source: source as 0 | 2 | 4,
                    description: `Demo ${source === 0 ? 'Risk' : source === 2 ? 'Issue' : 'Support Request'} ${i + 1}`,
                    id: projectIndex * 100 + i
                });
            }
        });

        // Generate demo notifications
        const notificationTypes: (0 | 1 | 2 | 3 | 4 | 5 | 6)[] = [0, 1, 2, 3]; // Action, Risk, Issue, Decision
        const numNotifications = Math.floor(Math.random() * 8) + 3; // 3-10 notifications

        for (let i = 0; i < numNotifications; i++) {
            const type = notificationTypes[Math.floor(Math.random() * notificationTypes.length)];
            const daysAgo = Math.floor(Math.random() * 30);
            const date = new Date();
            date.setDate(date.getDate() - daysAgo);
            const rating = Math.random() < 0.3 ? 2 : Math.random() < 0.7 ? 1 : 0;

            demoNotifications.push({
                id: i + 1,
                projectId: projectsList[Math.floor(Math.random() * projectsList.length)]?.id || 0,
                type: type,
                description: `Demo notification ${i + 1}`,
                date: date,
                rating: rating as 0 | 1 | 2,
                seen: Math.random() < 0.4
            });
        }

        const scopeStatuses = demoWorkstreams.map(ws => ws.workstreamHealth.scope);
        const scheduleStatuses = demoWorkstreams.map(ws => ws.workstreamHealth.schedule);
        const resourcesStatuses = demoWorkstreams.map(ws => ws.workstreamHealth.resources);
        const financialStatuses = demoWorkstreams.map(ws => ws.workstreamHealth.financial);
        const otherStatuses = demoWorkstreams.map(ws => ws.workstreamHealth.other);
        const overallStatuses = demoWorkstreams.map(ws => ws.workstreamHealth.overall);

        const projectHealth: WorkstreamHealth = {
            workstreamId: 0,
            workstreamName: undefined,
            lifecycleStage: undefined,
            scope: calculateWorstStatus(scopeStatuses),
            schedule: calculateWorstStatus(scheduleStatuses),
            resources: calculateWorstStatus(resourcesStatuses),
            financial: calculateWorstStatus(financialStatuses),
            other: calculateWorstStatus(otherStatuses),
            overall: calculateWorstStatus(overallStatuses)
        };

        const demoData: DashboardData = {
            workstreams: demoWorkstreams,
            projectHealth: projectHealth,
            projectOverview: demoProjectOverview,
            currentWeek: Math.floor(Math.random() * 20) + 10,
            totalWeeks: Math.floor(Math.random() * 30) + 40,
            notifications: demoNotifications
        };

        return demoData;
    }

    function filterDemoDataByProjects(fullData: DashboardData, projectIds: number[]): DashboardData {
        // Filter workstreams based on selected projects
        const filteredWorkstreams = fullData.workstreams.filter(ws =>
            projectIds.includes(ws.workstreamHealth.workstreamId)
        );

        // Filter project overview based on selected projects
        const filteredOverview = fullData.projectOverview.filter(po =>
            po.workstreamId && projectIds.includes(po.workstreamId)
        );

        // Filter notifications based on selected projects
        const filteredNotifications = fullData.notifications.filter(notif =>
            projectIds.includes(notif.projectId)
        );

        const scopeStatuses = filteredWorkstreams.map(ws => ws.workstreamHealth.scope);
        const scheduleStatuses = filteredWorkstreams.map(ws => ws.workstreamHealth.schedule);
        const resourcesStatuses = filteredWorkstreams.map(ws => ws.workstreamHealth.resources);
        const financialStatuses = filteredWorkstreams.map(ws => ws.workstreamHealth.financial);
        const otherStatuses = filteredWorkstreams.map(ws => ws.workstreamHealth.other);
        const overallStatuses = filteredWorkstreams.map(ws => ws.workstreamHealth.overall);

        const projectScope = calculateWorstStatus(scopeStatuses);
        const projectSchedule = calculateWorstStatus(scheduleStatuses);
        const projectResources = calculateWorstStatus(resourcesStatuses);
        const projectFinancial = calculateWorstStatus(financialStatuses);
        const projectOther = calculateWorstStatus(otherStatuses);
        const projectOverallStatus = calculateWorstStatus(overallStatuses);

        return {
            workstreams: filteredWorkstreams,
            projectHealth: {
                workstreamId: 0,
                workstreamName: undefined,
                lifecycleStage: undefined,
                scope: projectScope,
                schedule: projectSchedule,
                resources: projectResources,
                financial: projectFinancial,
                other: projectOther,
                overall: projectOverallStatus
            },
            projectOverview: filteredOverview,
            currentWeek: fullData.currentWeek,
            totalWeeks: fullData.totalWeeks,
            notifications: filteredNotifications
        };
    }

    function toggleDemoMode(enabled: boolean) {
        if (enabled) {
            // Generate demo projects
            const demoProjList = generateDemoProjects();
            setDemoProjects(demoProjList);

            // Select all demo projects by default
            const allDemoProjectIds = demoProjList.map(p => p.id);
            setSelectedProjects(allDemoProjectIds);

            // Generate demo data for all projects
            const demoData = generateDemoDataForProjects(demoProjList);
            setDemoMode(true);
            // Clear existing data first
            setWorkstreamProgress([]);
            setProjectProgressActual(0);
            setProjectProgressPlanned(0);
            setProjectHealth(undefined);
            setWorkstreamHealth([]);
            setProjectOverview([]);
            setCurrentWeek(0);
            setTotalWeeks(0);
            setNotifications([]);
            // Store both full and filtered demo data
            setFullDemoDashboardData(demoData);
            setDemoDashboardData(demoData);
            processDashboardData(demoData);
        } else {
            // Exit demo mode - first set demo mode to false
            setDemoMode(false);
            // Clear demo data
            setDemoDashboardData(null);
            setFullDemoDashboardData(null);
            setDemoProjects([]);
            // Restore user's original project selection
            const originalProjects = app.applicationUser?.userSettings?.dashboardProjects || app.projects.map(p => p.id);
            setSelectedProjects(originalProjects);
            // Clear current display
            setWorkstreamProgress([]);
            setProjectProgressActual(0);
            setProjectProgressPlanned(0);
            setProjectHealth(undefined);
            setWorkstreamHealth([]);
            setProjectOverview([]);
            setCurrentWeek(0);
            setTotalWeeks(0);
            setNotifications([]);
            // Force reload of real data
            setIsLoading(true);
            if (originalProjects.length > 0) {
                const queryString = originalProjects.map(id => `ids=${id}`).join('&');
                fetch(`Reports/MultiProjectDashboard?${queryString}`)
                    .then(async (response) => {
                        if (!response.ok) {
                            const errorText = await response.text();
                            console.error("Server error:", response.status, errorText);
                            throw new Error(`Server returned ${response.status}: ${errorText.substring(0, 200)}`);
                        }
                        return response.json();
                    })
                    .then(data => {
                        processDashboardData(data);
                        setIsLoading(false);
                    })
                    .catch((error) => {
                        console.error("Error fetching dashboard data:", error);
                        setIsLoading(false);
                    });
            }
        }
    }

    if (isLoading) {
        return <Loading />
    }
    if (app.applicationUser && app.applicationUser.projectUserRoles.length == 0) {
        return <NoProjects />
    }

    // Mobile version - simplified layout
    if (isMobile) {
        return (
            <div className="flex flex-col h-full overflow-auto relative" style={{ zoom: zoomLevel }}>
                <div className="sticky top-0 z-10 bg-background border-b px-2 py-2 space-y-2">
                    {demoMode && (
                        <div className="flex justify-center">
                            <button
                                id="exitDemoMode"
                                className="rounded-md text-white bg-red-500 px-3 py-1 w-full max-w-xs"
                                onClick={() => toggleDemoMode(false)}
                            >
                                Exit Demo Mode
                            </button>
                        </div>
                    )}
                    {/* <div className="flex items-center justify-between">
                        <div className="fixed top-2 right-2 z-50">
                            <button
                                className="p-2 rounded-full bg-blue-600"
                                onClick={() => { loadDashboardData(); }}
                            >
                                <RefreshIcon className="w-7 h-7" />
                            </button>
                        </div>
                    </div> */}
                    <div className="flex gap-1">
                        {demoMode && selectedProjects.length < demoProjects.length && (
                            <button
                                onClick={() => resetFilter()}
                                className="flex-1 button-default whitespace-nowrap"
                            >
                                Show All
                            </button>
                        )}
                        {!demoMode && selectedProjects.length < app.projects.length && (
                            <button
                                onClick={() => resetFilter()}
                                className="flex-1 button-default whitespace-nowrap"
                            >
                                Show All
                            </button>
                        )}
                        <button
                            onClick={showDashboardOptions}
                            className="flex-1 button-default"
                        >
                            Projects
                        </button>
                        <button
                            onClick={showViewOptions}
                            className="flex-1 button-default"
                        >
                            View
                        </button>
                    </div>
                </div>
                <div className="p-2 space-y-4 mb-8">
                    {(selectedViewElements & 4) === 4 && projectHealth && <ProjectHealth projectHealth={projectHealth} currentWeek={currentWeek} totalWeeks={totalWeeks} notifications={notifications} setNotifications={setNotifications} isMultiProject={true} handleHealthClick={filterProjectsByHealth} /*demoMode={demoMode} */ />}

                    {(selectedViewElements & 1) === 1 && (
                        <div className="w-full">
                            <RadialChartComponent actualPercentage={projectProgressActual} plannedPercentage={projectProgressPlanned} isMultiProject={true} onClick={radialClick} isMobile={isMobile} />
                        </div>
                    )}
                    {(selectedViewElements & 2) === 2 && projectOverview && <OverviewComponent projectOverviewRows={projectOverview} isMultiProject={true} />}
                    {(selectedViewElements & 8) === 8 && <WorkstreamProgressChart workstreamProgress={workstreamProgress} isMultiProject={true} />}
                    {(selectedViewElements & 16) === 16 && <WorkstreamHealthReport workstreamHealth={workstreamHealth} isMultiProject={true} showProjectName={(selectedViewElements & 8) !== 8} demoMode={demoMode} />}
                </div>
                <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                    <DialogContent className="flex flex-col w-[95vw] max-w-none max-h-[90vh] overflow-y-auto">
                        <DialogHeader>
                            <DialogTitle className="text-xl">{dialogTitle}</DialogTitle>
                        </DialogHeader>
                        {dialogContent}
                    </DialogContent>
                </Dialog>
            </div>
        );
    }

    const contextValue = {
        toggleDemoMode
    };
    // Desktop/Tablet version - with zoom
    return (
        <MultiProjectDashContext.Provider value={contextValue}>
        <div
            className="flex flex-col h-full overflow-auto"
            style={{
                zoom: zoomLevel
            }}
        >
            {wizard && wizard.getWizardPointer()}
            <div className={"flex flex-row justify-between items-center px-4 py-2" + (theme === "light" ? " glassmorphism-card" : "")}>
                <div className="flex flex-row items-center gap-2">
                    <h1 className="text-3xl font-bold">Multi Project Dashboard</h1>
                    <div className='flex flex-row items-center'>
                        <button
                            id="refreshDashboardButton"
                            className="text-3xl font-bold text-white p-1 my-auto rounded-full hover:bg-accent"
                            onClick={() => { loadDashboardData(); }}
                        >
                            <RefreshIcon className="" />
                        </button>
                        <HelpLink page="MultiProjectDash" />
                    </div>
                    {demoMode && (
                        <button
                            id="exitDemoMode"
                            className="rounded-md text-white bg-red-500 h-min px-3 py-1 my-auto ml-2"
                            onClick={() => toggleDemoMode(false)}
                        >
                            Exit Demo Mode
                        </button>
                    )}
                </div>
                <div className="flex flex-row gap-4">
                    {demoMode && selectedProjects.length < demoProjects.length && (
                        <button
                            id="showAllProjectsButton"
                            onClick={() => resetFilter()}
                            className="button-default"
                        >
                            Show All Projects
                        </button>
                    )}
                    {!demoMode && selectedProjects.length < app.projects.length && (
                        <button
                            id="showAllProjectsButton"
                            onClick={() => resetFilter()}
                            className="button-default"
                        >
                            Show All Projects
                        </button>
                    )}
                    <button
                        id="selectProjectsButton"
                        onClick={showDashboardOptions}
                        className="button-default"
                    >
                        Select Projects
                    </button>
                    <button
                        id="viewButton"
                        onClick={showViewOptions}
                        className="button-default"
                    >
                        View
                    </button>
                </div>
            </div>
            <div className="flex flex-col h-min justify-normal gap-4 py-2 my-auto">
                <div className="flex flex-row justify-normal gap-4 px-4 my-auto">
                    {(selectedViewElements & 1) === 1 && <RadialChartComponent actualPercentage={projectProgressActual} plannedPercentage={projectProgressPlanned} isMultiProject={true} onClick={radialClick} />}
                    {(selectedViewElements & 2) === 2 && projectOverview && <OverviewComponent projectOverviewRows={projectOverview} isMultiProject={true} demoMode={demoMode} />}
                    {(selectedViewElements & 4) === 4 && projectHealth && <ProjectHealth projectHealth={projectHealth} currentWeek={currentWeek} totalWeeks={totalWeeks} notifications={notifications} setNotifications={setNotifications} isMultiProject={true} handleHealthClick={filterProjectsByHealth} /* demoMode={demoMode} */ />}
                </div>

                <div className="flex flex-row justify-normal gap-4 px-4 my-auto">
                    {(selectedViewElements & 8) === 8 && <WorkstreamProgressChart workstreamProgress={workstreamProgress} isMultiProject={true} />}
                    {(selectedViewElements & 16) === 16 && <WorkstreamHealthReport workstreamHealth={workstreamHealth} isMultiProject={true} showProjectName={(selectedViewElements & 8) !== 8} demoMode={demoMode} />}
                </div>
                {wizard.getTourControls()}
            </div>
            <TutorialChecker
                tutorialId={Tutorial.Dashboard}
                tutorial={async () => {
                    toggleDemoMode(true);
                    wizard.setWizardSteps(getBasicTourSteps);
                    wizard.setGuidedTourCompleteFunctionCall(() => () => { toggleDemoMode(false); });
                    wizard.startGuidedTour();
                }} />
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogContent className="flex flex-col max-w-fit">
                    <DialogHeader>
                        <DialogTitle>{dialogTitle}</DialogTitle>
                    </DialogHeader>
                    {dialogContent}
                </DialogContent>
            </Dialog>
        </div>
        </MultiProjectDashContext.Provider>
    );
}

function MultiProjectDash() {
    return <WizardProvider wizardSteps={getBasicTourSteps()}>
        <MultiProjectDashboard />
    </WizardProvider>
} 

export default MultiProjectDash